// ── participant-access-history-collection: correlated staff history rows ─────

import { useMemo } from "react";
import { useCollection, type CollectionColumnDef, type UseCollectionResult } from "@vjal/ui";

import type { CoachingHistoryEventDto } from "../../../services/coaching-dtos";
import { orDash } from "../participants-helpers";

export const PARTICIPANT_ACCESS_HISTORY_PAGE_SIZE = 25;
export const PARTICIPANT_ACCESS_HISTORY_DEFAULT_SORT = { id: "occurred_at", dir: "desc" as const };

export type ParticipantAccessHistoryRow = CoachingHistoryEventDto & Record<string, unknown>;

export function buildParticipantAccessHistoryCollectionColumns(): CollectionColumnDef<ParticipantAccessHistoryRow>[] {
  return [
    {
      id: "occurred_at",
      header: "When",
      sortable: true,
      sortAccessor: (row) => row.occurred_at ?? "",
      priority: 1,
      cell: (row) => orDash(row.occurred_at)
    },
    {
      id: "action",
      header: "Action",
      sortable: true,
      sortAccessor: (row) => row.action ?? "",
      priority: 2,
      cell: (row) => orDash(row.action)
    },
    {
      id: "summary",
      header: "Summary",
      sortable: true,
      sortAccessor: (row) => row.summary ?? "",
      searchValue: (row) => `${row.summary ?? ""} ${row.action ?? ""}`,
      priority: 1,
      cell: (row) => orDash(row.summary)
    }
  ];
}

export function useParticipantAccessHistoryCollection(
  rows: ParticipantAccessHistoryRow[]
): UseCollectionResult<ParticipantAccessHistoryRow> {
  const columns = useMemo(() => buildParticipantAccessHistoryCollectionColumns(), []);

  return useCollection<ParticipantAccessHistoryRow>({
    mode: "client",
    rows,
    columns,
    pageSize: PARTICIPANT_ACCESS_HISTORY_PAGE_SIZE,
    urlSync: false,
    initialState: { sort: PARTICIPANT_ACCESS_HISTORY_DEFAULT_SORT },
    getRowKey: (row) => row.id
  });
}